import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { IconBack, IconCheck, IconCopy } from "./icons";

export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

type Variant = "primary" | "accent" | "secondary" | "outline" | "ghost" | "danger" | "success" | "light" | "glass";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  // Nuit: the main action is a light pill (fg), dark text on it.
  primary: "bg-ink text-ink-fg hover:bg-ink/88",
  // The accent: rare (the agent's reaction badge, an active switch, one warm call to action).
  accent: "bg-accent text-accent-fg hover:bg-accent-hover",
  // Secondary: a chip (#2A2A2A).
  secondary: "bg-chip text-fg hover:bg-[#333333]",
  // « Non »: transparent with a 1 px #3A3A3A ring.
  outline: "bg-transparent text-fg shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-chip",
  ghost: "text-muted hover:text-fg hover:bg-chip",
  danger: "bg-danger-soft text-danger border border-transparent hover:border-danger/40",
  success: "bg-success text-[#111111] hover:brightness-110",
  // On the stage of the live screen (/ordinateur): the light call to action, and the translucent controls.
  light: "bg-[#fcfaf6] text-[#23211c] hover:bg-[#fcfaf6]/90",
  glass: "bg-white/10 text-white border border-white/15 hover:bg-white/18 aria-pressed:bg-[#fcfaf6] aria-pressed:text-[#23211c]",
};

// Phones get 44 px tap targets whatever the size; from `sm` up, the compact desktop sizes.
const SIZES: Record<Size, string> = {
  sm: "h-11 px-3.5 text-[14px] gap-1.5 sm:h-9 sm:px-3.5 sm:text-[13.5px]",
  md: "h-11 px-[18px] text-[14.5px] gap-2",
  lg: "h-12 px-6 text-[15px] gap-2",
};
// Every button is a pill (radius = height / 2).
const CORNERS: Record<Size, string> = { sm: "rounded-full", md: "rounded-full", lg: "rounded-full" };

/** `pill`: fully rounded (the controls of the live screen). */
export function buttonClass(variant: Variant = "primary", size: Size = "md", extra?: string, pill = false): string {
  return cx(
    "inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap select-none transition-[background,filter,border,color] disabled:pointer-events-none disabled:opacity-50",
    VARIANTS[variant],
    SIZES[size],
    pill ? "rounded-full" : CORNERS[size],
    extra,
  );
}

/** Button that keeps its label while busy (the label is how people and tests find it). */
export function Button({
  variant = "primary",
  size = "md",
  busy = false,
  pill = false,
  className,
  children,
  disabled,
  type = "button",
  ...props
}: ComponentProps<"button"> & { variant?: Variant; size?: Size; busy?: boolean; pill?: boolean }) {
  return (
    <button type={type} className={buttonClass(variant, size, className, pill)} disabled={disabled || busy} aria-busy={busy || undefined} {...props}>
      {busy ? <Spinner className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
}

export function LinkButton({
  variant = "primary",
  size = "md",
  className,
  ...props
}: ComponentProps<typeof Link> & { variant?: Variant; size?: Size; className?: string }) {
  return <Link className={buttonClass(variant, size, className)} {...props} />;
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx("animate-spin", className ?? "h-4 w-4")} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** A flat panel (#1A1A1A), no rim, no shadow. */
export function Card({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cx("min-w-0 rounded-[20px] bg-elev", className)} {...props}>
      {children}
    </div>
  );
}

export function PageHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cx(className?.includes("mb-") ? null : "mb-6", "flex flex-wrap items-center justify-between gap-3", className)}>
      <div className="min-w-0">
        <h1 className="font-display text-[28px] leading-tight font-medium">{title}</h1>
        {description ? <p className="mt-1 max-w-2xl text-[14px] leading-normal text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * The head of a page opened from the conversation (the V2 page boards): a round back button to the conversation, the
 * title in Newsreader 28, and the page's own round buttons on the right.
 */
export function PageTop({ title, actions, after, back = "/", className }: { title: ReactNode; actions?: ReactNode; after?: ReactNode; back?: "/" | "/agents" | "/missions" | "/reglages"; className?: string }) {
  return (
    <header className={cx("mb-5 flex min-h-11 items-center gap-3", className)}>
      <Link to={back} aria-label="Retour" className={ROUND_BUTTON}>
        <IconBack width={16} height={16} strokeWidth={2.4} />
      </Link>
      <h1 className="min-w-0 truncate font-display text-[28px] leading-tight font-medium">{title}</h1>
      {after}
      <span className="flex-1" />
      {actions}
    </header>
  );
}

/** A round 36 px button (40 px tap area on a phone): back, add, settings. */
export const ROUND_BUTTON =
  "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-chip text-fg transition hover:bg-[#333333] after:absolute after:-inset-1 after:content-[''] max-md:h-11 max-md:w-11";
/** The same, light (the page's main action: « + »). */
export const ROUND_BUTTON_PRIMARY =
  "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-ink-fg transition hover:bg-ink/88 after:absolute after:-inset-1 after:content-[''] max-md:h-11 max-md:w-11";

/** A section label (13 px, 600, faint): « Routines », « En cours », « Ton rythme »… */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx("px-1 text-[13px] font-semibold text-subtle", className)}>{children}</p>;
}

/** A panel of rows separated by 1 px lines (Réglages, Connexions, routines). */
export function RowList({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("min-w-0 rounded-[20px] bg-elev px-4 py-1 [&>*+*]:border-t [&>*+*]:border-line", className)}>{children}</div>;
}

/** A titled card of a settings-like page (h2); `id` makes it an anchor of the page's own navigation. */
export function Panel({
  title,
  description,
  children,
  id,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <Card id={id} className={cx("scroll-mt-24 p-5 sm:p-6", className)}>
      <h2 className="text-[16px] leading-snug font-semibold">{title}</h2>
      {description ? <p className="mt-1 text-[13.5px] leading-relaxed text-muted">{description}</p> : null}
      <div className="mt-4 space-y-4">{children}</div>
    </Card>
  );
}

/** A titled block of a page (h2). */
export function Section({
  title,
  description,
  action,
  children,
  className,
  id,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section className={cx("min-w-0", className)} id={id}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 className="px-1 text-[13px] leading-snug font-semibold text-subtle">{title}</h2>
          {description ? <p className="mt-0.5 px-1 text-[13px] text-muted">{description}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

const TONES: Record<Tone, string> = {
  neutral: "bg-sunken text-muted",
  accent: "bg-accent-soft text-accent-ink",
  success: "bg-success-soft text-success-ink",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
  info: "bg-info-soft text-info",
};

export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cx("inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-semibold whitespace-nowrap", TONES[tone], className)}>
      {children}
    </span>
  );
}

export function Notice({ tone = "neutral", children, className, role }: { tone?: Tone; children: ReactNode; className?: string; role?: "status" | "alert" }) {
  return (
    <div role={role} className={cx("rounded-2xl px-4 py-3 text-[14px] leading-relaxed wrap-anywhere", TONES[tone], className)}>
      {children}
    </div>
  );
}

/** Error banner with an optional retry. */
export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Notice tone="danger" role="alert" className="flex flex-wrap items-center justify-between gap-3">
      <span>{message}</span>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Réessayer
        </Button>
      ) : null}
    </Notice>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[20px] px-6 py-10 text-center">
      {/* No emoji in the interface: a text icon (an emoji passed by an older page) is not drawn. */}
      {icon && typeof icon !== "string" ? (
        <div className="mb-3 text-3xl" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <p className="font-display text-[22px] leading-snug">{title}</p>
      {children ? <div className="mt-1.5 max-w-md text-[14px] leading-relaxed text-muted">{children}</div> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function Loading({ label = "Chargement…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-[13.5px] text-muted" role="status">
      <Spinner />
      {label}
    </div>
  );
}

const fieldBase =
  "w-full min-w-0 rounded-[14px] border border-line bg-elev px-3.5 text-[15px] text-fg placeholder:text-subtle outline-none transition focus:border-line-strong focus:ring-4 focus:ring-[var(--glow)] disabled:opacity-60 sm:text-[14.5px]";

export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input className={cx(fieldBase, "h-11", className)} {...props} />;
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return <textarea className={cx(fieldBase, "min-h-24 py-2.5 leading-relaxed", className)} {...props} />;
}

/** The chevron drawn at the right of a select (a data URI: no request). */
export const SELECT_ARROW =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23a3a3a3'%3E%3Cpath d='M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4z'/%3E%3C/svg%3E\")";

export function Select({ className, children, style, ...props }: ComponentProps<"select">) {
  return (
    <select
      className={cx(fieldBase, "h-11 appearance-none bg-[length:16px] bg-[right_12px_center] bg-no-repeat pr-9", className)}
      style={{ backgroundImage: SELECT_ARROW, ...style }}
      {...props}
    >
      {children}
    </select>
  );
}

/** Label + control + hint/error. Pass the control's id as `htmlFor`. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("min-w-0 space-y-1.5", className)}>
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-fg">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[12.5px] text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12px] leading-snug text-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

/** A real checkbox drawn as a switch, with its label and an optional description. */
export function Switch({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-[15.5px] font-semibold">
          {label}
        </label>
        {description ? (
          <p id={`${id}-desc`} className="mt-0.5 text-[13.5px] text-muted">
            {description}
          </p>
        ) : null}
      </div>
      <input
        id={id}
        type="checkbox"
        className="switch"
        checked={checked}
        disabled={disabled}
        aria-describedby={description ? `${id}-desc` : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  );
}

/** A real radio stretched invisibly over its whole label (the label is the drawing; clicks land on the radio). */
export const COVER_RADIO = "absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none rounded-[inherit] opacity-0";

/**
 * Radio drawn as a selectable card. The radio's accessible name is exactly `label`
 * (the description is attached with aria-describedby).
 */
export function RadioCard({
  name,
  value,
  label,
  description,
  checked,
  onChange,
  children,
  disabled,
}: {
  name: string;
  value: string;
  label: string;
  description?: ReactNode;
  checked: boolean;
  onChange: (value: string) => void;
  children?: ReactNode;
  disabled?: boolean;
}) {
  const base = `${name}-${value}`.replace(/[^A-Za-z0-9_-]/g, "_");
  return (
    <label
      className={cx(
        "relative flex min-w-0 items-start gap-3 rounded-[18px] border bg-elev p-4 transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent",
        checked ? "border-ink shadow-[inset_0_0_0_0.5px_var(--ink)]" : "border-transparent hover:bg-chip",
        disabled && "opacity-60",
      )}
    >
      <input
        type="radio"
        className={COVER_RADIO}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        aria-labelledby={`${base}-label`}
        aria-describedby={description || children ? `${base}-desc` : undefined}
        onChange={() => onChange(value)}
      />
      <span className="min-w-0">
        <span id={`${base}-label`} className="block text-[15.5px] font-semibold">
          {label}
        </span>
        {description || children ? (
          <span id={`${base}-desc`} className="mt-1 block text-[13.5px] leading-snug text-muted">
            {description}
            {children}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * Mutually exclusive choices drawn as pills: real radios (visually hidden) in a fieldset, so each choice is a radio
 * named by its label and the group is named by `legend`.
 */
export function Segmented<T extends string>({
  name,
  legend,
  value,
  options,
  onChange,
  hint,
  disabled,
}: {
  name: string;
  legend: ReactNode;
  value: T;
  options: readonly { value: T; label: string; title?: string }[];
  onChange: (value: T) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <fieldset className="min-w-0" disabled={disabled}>
      <legend className="mb-2 text-[13px] font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const checked = o.value === value;
          return (
            <label
              key={o.value}
              title={o.title}
              className={cx(
                "relative inline-flex min-h-11 items-center rounded-full border px-4 text-[14px] transition select-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent sm:min-h-9 sm:text-[13.5px]",
                checked ? "border-ink bg-ink font-semibold text-ink-fg" : "border-transparent bg-chip text-fg hover:bg-[#333333]",
              )}
            >
              <input type="radio" className={COVER_RADIO} name={name} value={o.value} checked={checked} onChange={() => onChange(o.value)} />
              {o.label}
            </label>
          );
        })}
      </div>
      {hint ? <p className="mt-1.5 text-[12px] leading-snug text-subtle">{hint}</p> : null}
    </fieldset>
  );
}

export function Orb({ size = 40, active = false, className, color }: { size?: number; active?: boolean; className?: string; color?: string }) {
  return (
    <span
      className={cx("orb", className)}
      data-active={active ? "true" : "false"}
      style={{ width: size, height: size, ...(color ? { ["--orb" as string]: color } : null) }}
      aria-hidden="true"
    />
  );
}

/** Copies `text` to the clipboard; the label is announced to assistive tech. */
export function CopyButton({ text, label = "Copier", className }: { text: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  return (
    <button
      type="button"
      className={cx(buttonClass("secondary", "sm"), className)}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 1800);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? <IconCheck width={14} height={14} /> : <IconCopy width={14} height={14} />}
      <span aria-live="polite">{copied ? "Copié" : label}</span>
    </button>
  );
}

/** Visually hidden text (still read by screen readers and present in the DOM). */
export function SrOnly({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

/** Monospace command block with a copy button; scrolls horizontally inside itself only. */
export function CommandLine({ command }: { command: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-[14px] border border-line bg-sunken p-2 pl-3">
      <code className="min-w-0 flex-1 overflow-x-auto font-mono text-[12.5px] whitespace-nowrap">{command}</code>
      <CopyButton text={command} />
    </div>
  );
}
